import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { ArrowLeft, FileText } from 'lucide-react';
import { Button, Chip } from '@/ui/primitives';
import { PageShell, Stack } from '@/ui/layout';
import { ApiError } from '@/lib/api-client';
import { formatCPF, formatName } from '@/lib/format';
import { useCliente, useDocumentos } from '@/modules/clientes/api';
import { decodeClienteId } from '@/modules/clientes/cliente-id';
import {
  buildChecks,
  buildSecoes,
  calcScore,
  detectarDuplicidades,
  docsDoImovel,
  docSituacao,
  statusLabel,
  statusTone,
  tipoDocLabel,
  type Duplicidade,
  type SecaoImovel,
} from '@/modules/clientes/conformidade';
import { ImovelDetailModal } from '@/modules/clientes/components/ImovelDetailModal';
import type { Documento } from '@/modules/clientes/schemas';

export const Route = createFileRoute('/_authed/clientes_/$id_/relatorio/conformidade-documental')({
  validateSearch: (search: Record<string, unknown>): { imprimir?: boolean } =>
    search.imprimir === true || search.imprimir === '1' ? { imprimir: true } : {},
  component: ConformidadeDocumentalRoute,
});

const RELATORIO_NOME = 'Relatório de Conformidade Documental';

const PRINT_CSS = `
@media print {
  body * { visibility: hidden !important; }
  #conformidade-print-area, #conformidade-print-area * { visibility: visible !important; }
  #conformidade-print-area {
    position: absolute !important;
    left: 0 !important;
    top: 0 !important;
    width: 100% !important;
    padding: 8px 12px !important;
  }
  .no-print { display: none !important; }
  .print-only { display: block !important; }
  @page { size: A4 portrait; margin: 10mm; }

  /* compacta o stack vertical: sem gaps grandes entre seções */
  #conformidade-print-area > * { gap: 6px !important; }
  #conformidade-print-area .imovel-card {
    margin: 0 !important;
    padding: 0 !important;
    page-break-inside: auto !important;
    break-inside: auto !important;
  }
  #conformidade-print-area .imovel-card > div { padding: 4px 0 !important; }
  #conformidade-print-area h2 {
    font-size: 11pt !important;
    margin: 0 !important;
    line-height: 1.2 !important;
  }
  #conformidade-print-area h3 {
    font-size: 7pt !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  #conformidade-print-area table {
    font-size: 7pt !important;
    width: 100% !important;
    table-layout: fixed !important;
    border-collapse: collapse !important;
    margin: 2px 0 !important;
  }
  #conformidade-print-area th,
  #conformidade-print-area td {
    padding: 2px 4px !important;
    vertical-align: top !important;
    word-wrap: break-word !important;
    overflow-wrap: anywhere !important;
    white-space: normal !important;
    font-size: 7pt !important;
    line-height: 1.25 !important;
  }
  #conformidade-print-area td * {
    font-size: 7pt !important;
    line-height: 1.25 !important;
  }
  #conformidade-print-area th { font-size: 6.5pt !important; }
  #conformidade-print-area .chip {
    font-size: 6pt !important;
    padding: 0 4px !important;
    white-space: normal !important;
  }

  #conformidade-print-area tr { page-break-inside: avoid !important; }
}
`;


function ConformidadeDocumentalRoute() {
  const { id } = Route.useParams();
  const { imprimir } = Route.useSearch();
  const cpf = decodeClienteId(id);
  const navigate = useNavigate();
  const { data, isLoading, error } = useCliente(cpf);
  const { data: docs = [], isLoading: docsLoading } = useDocumentos(cpf);
  const [imovelDetail, setImovelDetail] = useState<Record<string, unknown> | null>(null);
  const autoPrintExecutado = useRef(false);

  useEffect(() => {
    if (!data?.nome) return;
    const previousTitle = document.title;
    document.title = `${RELATORIO_NOME} - ${formatName(data.nome)}`;
    return () => {
      document.title = previousTitle;
    };
  }, [data?.nome]);

  useEffect(() => {
    if (!imprimir || isLoading || docsLoading || !data || autoPrintExecutado.current) return;
    autoPrintExecutado.current = true;
    const timeout = window.setTimeout(() => window.print(), 300);
    return () => window.clearTimeout(timeout);
  }, [data, docsLoading, imprimir, isLoading]);

  const back = (
    <Button
      variant="ghost"
      onClick={() => {
        if (window.history.length > 1) window.history.back();
        else void navigate({ to: '/clientes/$id', params: { id }, search: { relatorio: undefined, tab: 'relatorios', from_pipe: undefined, from_grupo: undefined } });
      }}
    >
      <ArrowLeft size={14} strokeWidth={1.5} />
      Voltar ao cliente
    </Button>
  );

  if (isLoading) {
    return (
      <PageShell eyebrow="Relatórios" title="Conformidade Documental" back={back}>
        <p className="text-sm text-text-muted">Carregando…</p>
      </PageShell>
    );
  }

  if (error || !data) {
    return (
      <PageShell eyebrow="Relatórios" title="Conformidade Documental" back={back}>
        <p className="text-sm text-danger">
          {error instanceof ApiError ? error.message : 'Falha ao carregar cliente.'}
        </p>
      </PageShell>
    );
  }

  const secoes = buildSecoes(data);
  const duplicidades = detectarDuplicidades(secoes);
  const totalDuplicados = duplicidades.size;
  const municipioUf = [data.municipio ? formatName(data.municipio) : null, data.uf]
    .filter(Boolean)
    .join(' / ');

  return (
    <PageShell eyebrow="Relatórios" title="Conformidade Documental" back={back}>
      <style>{PRINT_CSS}</style>
      <div id="conformidade-print-area">
        <Stack gap="md">
          <h1
            className="print-only text-text font-extrabold"
            style={{
              display: 'none',
              fontSize: '14pt',
              letterSpacing: '-0.02em',
              margin: '0 0 6px 0',
            }}
          >
            Relatório de Conformidade Documental
          </h1>
          <div className="flex flex-col gap-1.5">
            <div className="flex items-baseline gap-2">
              <span
                className="uppercase font-semibold text-text-muted"
                style={{ fontSize: '0.7rem', letterSpacing: '0.06em' }}
              >
                Cliente:
              </span>
              <span className="text-text font-semibold" style={{ fontSize: '0.95rem' }}>
                {formatName(data.nome)}
              </span>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span
                className="uppercase font-semibold text-text-muted"
                style={{ fontSize: '0.7rem', letterSpacing: '0.06em' }}
              >
                CPF:
              </span>
              <span className="text-text" style={{ fontSize: '0.9rem' }}>
                {formatCPF(data.cpf)}
              </span>
              {municipioUf && (
                <Chip tone="info" size="sm">
                  {municipioUf}
                </Chip>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => window.print()}
              className="chip chip-danger cursor-pointer inline-flex items-center gap-1 no-print"
              style={{ fontSize: '0.78rem' }}
              aria-label="Gerar PDF"
            >
              <FileText size={11} strokeWidth={1.8} />
              PDF
            </button>
            {totalDuplicados > 0 && (
              <Chip tone="warn" size="sm">
                {totalDuplicados} {totalDuplicados === 1 ? 'imóvel' : 'imóveis'} em duplicidade
              </Chip>
            )}
          </div>

          {secoes.length === 0 ? (
            <div className="empty-state py-10">
              <p className="empty-state-title">Sem imóveis</p>
              <p className="empty-state-desc">Adicione um imóvel ou arrendamento.</p>
            </div>
          ) : (
            secoes.map((s) => (
              <ImovelSection
                key={s.key}
                secao={s}
                docs={docsDoImovel(docs, s)}
                duplicidade={duplicidades.get(s.key) ?? null}
                onOpen={() => setImovelDetail(s.imovel)}
              />
            ))
          )}
        </Stack>
      </div>
      <ImovelDetailModal
        imovel={imovelDetail}
        cpfDono={data.cpf}
        nomeDono={data.nome}
        onClose={() => setImovelDetail(null)}
      />
    </PageShell>
  );
}

function ImovelSection({
  secao,
  docs,
  duplicidade,
  onOpen,
}: {
  secao: SecaoImovel;
  docs: Documento[];
  duplicidade: Duplicidade | null;
  onOpen: () => void;
}) {
  const checks = buildChecks(secao.imovel, docs);
  const score = calcScore(checks);
  const scoreTone: 'ok' | 'warn' | 'danger' = score >= 80 ? 'ok' : score >= 50 ? 'warn' : 'danger';

  return (
    <div className="imovel-card overflow-hidden">
      <div
        onClick={onOpen}
        className="flex flex-col gap-2 pt-4 pb-3 cursor-pointer hover:bg-surface2 rounded-md transition-colors no-print -mx-2 px-2"
        title="Ver detalhes do imóvel"
      >
        <h2
          className="text-text font-extrabold leading-tight"
          style={{ fontSize: '1.25rem', letterSpacing: '-0.02em' }}
        >
          {secao.denominacao}
        </h2>
        <div className="flex items-center gap-1.5 flex-wrap">
          <Chip tone={secao.tipo === 'Próprio' ? 'ok' : 'accent'} size="sm">
            {secao.tipo}
          </Chip>
          {secao.matricula && (
            <Chip tone="info" size="sm">
              Matrícula {secao.matricula}
            </Chip>
          )}
          {secao.area != null && (
            <Chip tone="accent" size="sm">
              {secao.area.toLocaleString('pt-BR')} ha
            </Chip>
          )}
          <Chip tone={scoreTone} size="sm">
            {score}%
          </Chip>
          <Chip tone="neutral" size="sm">
            {docs.length} {docs.length === 1 ? 'documento' : 'documentos'}
          </Chip>
          {duplicidade && (
            <Chip
              tone="warn"
              size="sm"
              title={`Mesma ${duplicidade.motivos.join(' e ').toLowerCase()} em: ${duplicidade.irmaos.join(', ')}`}
            >
              Duplicidade · {duplicidade.motivos.join(' + ')}
            </Chip>
          )}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="table table-sm w-full">
          <thead>
            <tr>
              <th style={{ width: '24%' }}>Check</th>
              <th style={{ width: '14%' }}>Status</th>
              <th>Detalhe</th>
            </tr>
          </thead>
          <tbody>
            {checks.map((c) => (
              <tr key={c.label}>
                <td style={{ fontSize: '0.85rem', fontWeight: 500 }}>{c.label}</td>
                <td>
                  <Chip tone={statusTone(c.status)} size="sm">
                    {statusLabel(c.status)}
                  </Chip>
                </td>
                <td style={{ fontSize: '0.82rem' }} className="text-text-2">
                  {c.detail}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="pt-4 pb-2">
        <h3
          className="uppercase font-semibold text-text-muted"
          style={{ fontSize: '0.72rem', letterSpacing: '0.06em' }}
        >
          Documentos do imóvel
        </h3>
      </div>
      <div className="overflow-x-auto pb-4">
        <table className="table table-sm w-full">
          <thead>
            <tr>
              <th style={{ width: '22%' }}>Tipo</th>
              <th>Descrição</th>
              <th style={{ width: '24%' }}>Situação</th>
            </tr>
          </thead>
          <tbody>
            {docs.length === 0 ? (
              <tr>
                <td colSpan={3} className="text-text-muted" style={{ fontSize: '0.82rem' }}>
                  Nenhum documento.
                </td>
              </tr>
            ) : (
              docs.map((d) => {
                const sit = docSituacao(d);
                const desc = (d as Record<string, unknown>).descricao as string | null | undefined;
                return (
                  <tr key={String(d.id)}>
                    <td>
                      <Chip tone="info" size="sm">
                        {tipoDocLabel(d.tipo)}
                      </Chip>
                    </td>
                    <td style={{ fontSize: '0.82rem' }}>
                      {desc || d.arquivo || '—'}
                    </td>
                    <td>
                      <Chip tone={sit.tone} size="sm">
                        {sit.label}
                      </Chip>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
